import ApiService from '@/common/api.service'
import agencyNotesModule from './agencyNotesModule'
import agencyContactsModule from './agencyContactsModule'
import agencyAttachmentsModule from './agencyAttachmentsModel'
import agencyLocationsModule from './agencyLocationsModule'

import * as utils from '@/common/utils'
import * as notesHelpers from '@/store/notes/notes.helpers'
import * as contactsHelpers from '@/store/contacts/contacts.helpers'
import * as attachmentsHelpers from '@/store/attachments/attachments.helpers'
import * as locationsHelpers from '@/store/locations/locations.helpers'
import store from '@/store'

const module = 'admin/agenciesModule'
const notesModule = module + '/notes'
const contactsModule = module + '/contacts'
const attachmentsModule = module + '/attachments'
const locationsModule = module + '/locations'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    id: null,
    name: '',
    fin_account_no: '',
    description: '',
    partner: {
      id: null,
      name: null
    },
    created_at: '',
    updated_at: '',
    note: '',
    transferDetails: []
  }
}

function formDefaults () {
  return {
    partners: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param agency     Object
 * @param notes       Array|null
 * @param contacts    Array|null
 * @param attachments Array|null
 * @param locations   Array|null
 *
 * @return Object
 */
function transformCreate (agency, notes = null, contacts = null, attachments = null, locations = null) {
  return {
    id: agency.id,
    name: agency.name,
    fin_account_no: agency.fin_account_no,
    description: agency.description,
    partner_id: agency.partner.id,
    notes,
    contacts,
    attachments,
    locations
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param agency     Object
 *
 * @return Object
 */
function transformUpdate (agency) {
  return {
    id: agency.id,
    name: agency.name,
    fin_account_no: agency.fin_account_no,
    description: agency.description
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: {
    name: '',
    fin_account_no: '',
    description: ''
  },
  isModified: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return (state.data.name !== state.original.name) ||
      ((state.data.fin_account_no || null) !== state.original.fin_account_no) ||
      ((state.data.description || null) !== state.original.description)
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`agencies/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        contactsHelpers.setContacts(contactsModule, response.data.data.contacts)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        locationsHelpers.setLocations(locationsModule, response.data.data.locations)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`agencies/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        notesHelpers.purge(notesModule)
        contactsHelpers.purge(contactsModule)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.purge(attachmentsModule)
        locationsHelpers.purge(locationsModule)
        locationsHelpers.setLocationTypes(locationsModule, response.data.data.location_types)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`agencies/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        contactsHelpers.setContacts(contactsModule, response.data.data.contacts)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        locationsHelpers.setLocations(locationsModule, response.data.data.locations)
        locationsHelpers.setLocationTypes(locationsModule, response.data.data.location_types)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let agency = context.getters['get']

    let notes = []
    let note = context.getters['get'].note || null
    if (note !== null && note.trim() !== '') {
      notes.push(note)
    }

    let contacts = contactsHelpers.contacts(contactsModule)
    let attachments = attachmentsHelpers.attachments(attachmentsModule)
    let locations = locationsHelpers.locations(locationsModule)

    return ApiService
      .post(`agencies`, transformCreate(
        agency, notes, contacts, attachments, locations
      ))
      .then((response) => {
        notesHelpers.purge(notesModule)
        contactsHelpers.purgeContacts(contactsModule)
        attachmentsHelpers.purge(attachmentsModule)
        locationsHelpers.purgeLocations(locationsModule)
        context.commit('set', defaults())
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * Transfers Agency and assets to new partner.
   *
   * @param context
   * @param agencyId
   * @param partnerId
   * @returns {Promise}
   */
  performTransfer (context, { agencyId, partnerId }) {
    let data = {
      agency_id: agencyId,
      partner_id: partnerId
    }

    return ApiService.post(`agencies/transfer/perform`, data).then((response) => {
      utils.addRecentHistory('admin/agenciesTransfer', response.data.data.transferred)
    })
  },

  /**
   * Gathers information about agency being transferred.
   *
   * @param context
   * @param agencyId
   * @returns Promise
   */
  transferCheck (context, agencyId) {
    return ApiService.get(`agencies/transfer/check/${agencyId}`).then((response) => {
      context.commit('setTransferDetails', response.data.data)
    })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    let agency = context.getters['get']

    return ApiService
      .put(`agencies/${agency.id}`, transformUpdate(agency))
      .then((response) => {
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`agencies/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`agencies`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = {
      id: data.id,
      name: data.name,
      fin_account_no: data.fin_account_no,
      description: data.description,
      partner: data.partner,
      created_at: data.created_at,
      updated_at: data.updated_at,
      notes: data.notes,
      transferDetails: []
    }
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name,
      fin_account_no: data.fin_account_no,
      description: data.description
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the transferDetails of all affected
   * assets of the selected agency.
   *
   * @param state
   * @param data
   */
  setTransferDetails (state, data) {
    state.data.transferDetails = Object.assign([], data)
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.isModified = false
    state.original.name = ''
    state.original.fin_account_no = ''
    state.original.description = ''
    store.commit('admin/agenciesModule/attachments/purge')
    store.commit('admin/agenciesModule/attachments/resetFormState')
    store.commit('admin/agenciesModule/contacts/purge')
    store.commit('admin/agenciesModule/contacts/resetFormState')
    store.commit('admin/agenciesModule/locations/purge')
    store.commit('admin/agenciesModule/locations/resetFormState')
    store.commit('admin/agenciesModule/notes/purge')
    store.commit('admin/agenciesModule/notes/resetFormState')
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    notes: agencyNotesModule,
    contacts: agencyContactsModule,
    attachments: agencyAttachmentsModule,
    locations: agencyLocationsModule
  }
}



// WEBPACK FOOTER //
// ./src/store/administration/agencies/agenciesModule.js